import { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { PhoneInput } from '../common/PhoneInput';
import { TimePicker } from '../common/TimePicker';
import { todayDateString } from '../../utils/time';
import { isValidEmail } from '../../utils/phone';
import { VISIT_TYPES, SMS_CONSENT_SCRIPT_VERBAL } from '../../constants';
import { SmsConsentDisclosure } from '../common/SmsConsentDisclosure';
import type { Visit } from '../../types';

interface EditVisitModalProps {
  visit: Visit | null;
  open: boolean;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
}

export function EditVisitModal({ visit, open, onClose, onSave }: EditVisitModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [visitType, setVisitType] = useState('');
  const [date, setDate] = useState('');
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState('00');
  const [ampm, setAmpm] = useState('AM');
  const [duration, setDuration] = useState('30');
  const [error, setError] = useState('');

  const isScheduled = !!(visit?.isScheduled || visit?.scheduledAt);
  const medium = visit?.medium || 'video';

  useEffect(() => {
    if (open && visit) {
      setName(visit.patientName || '');
      setEmail(visit.patientEmail || '');
      setPhone(visit.patientPhone || '');
      setSmsConsent(!!visit.smsConsent);
      setVisitType(visit.visitType || '');
      setError('');

      if (isScheduled && visit.scheduledAt) {
        const d = new Date(visit.scheduledAt);
        setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
        const h = d.getHours();
        setHour(h === 0 ? 12 : h > 12 ? h - 12 : h);
        setMinute(String(d.getMinutes()).padStart(2, '0'));
        setAmpm(h < 12 ? 'AM' : 'PM');
        setDuration(String(visit.duration || 30));
      }
    }
  }, [open, visit, isScheduled]);

  if (!visit) return null;

  const mediumTitles: Record<string, string> = { video: 'Edit Video Visit', phone: 'Edit Phone Visit', sms: 'Edit SMS Text Visit' };
  const isSms = medium === 'sms';

  const handleSave = () => {
    setError('');
    if (!name.trim()) { setError('Patient name is required'); return; }
    if (isSms && !phone.trim()) { setError('Patient phone is required for SMS text visits'); return; }
    if (!isSms && !email.trim()) { setError('Patient email is required'); return; }
    if (email.trim() && !isValidEmail(email.trim())) { setError('Please enter a valid email address'); return; }

    const body: Record<string, unknown> = { patientName: name.trim(), patientEmail: email.trim() };
    if (phone.trim() && smsConsent) {
      body.patientPhone = phone.trim();
      body.smsConsent = { source: 'provider_attestation', consentedAt: Date.now() };
    } else {
      body.patientPhone = phone.trim() || '';
      if (!smsConsent) body.smsConsent = null;
    }

    if (isScheduled) {
      body.visitType = visitType || '';
      if (date) {
        let h24 = hour;
        if (ampm === 'AM' && hour === 12) h24 = 0;
        else if (ampm === 'PM' && hour !== 12) h24 = hour + 12;
        const scheduledAt = new Date(`${date}T${String(h24).padStart(2, '0')}:${minute}`).getTime();
        if (isNaN(scheduledAt) || scheduledAt < Date.now()) { setError('Please select a future date and time'); return; }
        body.scheduledAt = scheduledAt;
      }
      body.duration = parseInt(duration) || 30;
    }

    onSave(body);
  };

  const showConsentGroup = !isSms && phone.trim().length > 0;

  return (
    <Modal open={open} onClose={onClose} maxWidth="450px" ariaLabel={mediumTitles[medium]}>
      <h3 className="mb-1">{mediumTitles[medium] || 'Edit Visit'}</h3>
      {error && <div className="alert alert--error">{error}</div>}
      <div className="form-group">
        <label>Patient Name</label>
        <input type="text" maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="form-group">
        <label>{isSms ? 'Patient Email ' : 'Patient Email'}{isSms && <span className="text-muted text-sm">(optional)</span>}</label>
        <input type="email" maxLength={200} value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="form-group">
        <label>{isSms ? 'Patient Phone' : 'Patient Phone '}{!isSms && <span className="text-muted text-sm">(optional)</span>}</label>
        <PhoneInput value={phone} onChange={setPhone} />
      </div>
      {showConsentGroup && (
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 'normal' }}>
            <input type="checkbox" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)} style={{ flexShrink: 0, width: '16px', height: '16px' }} />
            <span className="text-sm">Patient has consented to receive SMS reminders <SmsConsentDisclosure intro="Read this to the patient for their consent:" script={SMS_CONSENT_SCRIPT_VERBAL} /></span>
          </label>
        </div>
      )}
      {isScheduled && (
        <>
          <div className="form-group">
            <label>Visit Type <span className="text-muted text-sm">(optional)</span></label>
            <select value={visitType} onChange={(e) => setVisitType(e.target.value)}>
              <option value="">— Select —</option>
              {VISIT_TYPES.filter(Boolean).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} min={todayDateString()} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Time</label>
              <TimePicker hour={hour} minute={minute} ampm={ampm} onHourChange={setHour} onMinuteChange={setMinute} onAmpmChange={setAmpm} fullSize />
            </div>
          </div>
          {!isSms && (
            <div className="form-group">
              <label>Duration</label>
              <select value={duration} onChange={(e) => setDuration(e.target.value)}>
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">60 minutes</option>
              </select>
            </div>
          )}
        </>
      )}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button className="btn btn--outline btn--sm" onClick={onClose}>Cancel</button>
        <button className="btn btn--primary btn--sm" onClick={handleSave}>Save</button>
      </div>
    </Modal>
  );
}
